import { Stack, Typography, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';

import WeeklyHours from './availability/weeklyHours';
import { ScheduleDataObject, ScheduleDataObjectFromAPI } from './calendar/editAvailability';
import SelectDurationDropdown from './common/editAvailability/selectDuration';
import { convertTimeRangeHourFormat, timeToMinutes } from './common/helpers';

import { useAuth } from '../../../contexts/auth/authContext';
import { IntervalsObject } from '../../../interfaces/meeting';
import { setCanAddInterval } from '../../../slices/meetings';


const styles = {
  container1: {
    padding: '24px 20px',
  }, 
  headerText: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '18px',
  },
  bodyText1: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '14px',
    color: '#F83B3B',
  },
  buttonStyle: {
    textTransform: 'none',
    bgcolor: '#2449FC',
    borderRadius: '50px',
    color: '#FFFFFF',
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '16px',
    '&:hover': {
      bgcolor: '#FFFFFF',
      color: '#2449FC',
      border: '1px solid #2449FC',
    },
  },
  buttonTextOnly: {
    bgcolor: 'transparent',
    color: '#F83B3B',
    fontWeight: 500,
    '&:hover': {
      bgcolor: 'transparent',
      color: '#F83B3B',
      border: 'none',
    },
  },
};

const transformDataFromAPI = (dataFromAPI: ScheduleDataObjectFromAPI[] ):ScheduleDataObject  => {
  if (dataFromAPI && dataFromAPI.length > 0) {
    const scheduleData = dataFromAPI.
      find(item => item.weekdays && item.weekdays.length > 0);
    if (scheduleData) {
      return ({
        id: scheduleData?.id,
        dateStart: scheduleData?.date_start,
        dateEnd: scheduleData?.date_end,
        timeSlots: convertTimeRangeHourFormat(scheduleData?.time_slots, true),
        weekdays: scheduleData?.weekdays,
        duration: timeToMinutes(scheduleData?.duration),
        durationType: scheduleData?.duration_type,
        tenant: scheduleData?.tenant,
        type: scheduleData?.type,
      });
    }
  }
  return ({
    id: '',
    dateStart: '',
    dateEnd: '',
    timeSlots: [],
    weekdays: [],
    duration: null,
    durationType: null,
    tenant: null,
    type: null,
  });
};

const ListCalendarView = () => {
  const { slug } = useAuth();
  const dispatch = useDispatch();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [scheduleData, setScheduleData] = useState<ScheduleDataObject>({
    id: '',
    dateStart: '',
    dateEnd: '',
    timeSlots: [],
    weekdays: [],
    duration: null,
    durationType: null,
    tenant: null,
    type: null,
  });

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const { data, isLoading, error } = useSWR(companySlug ? `schedule/availability/tenant/${companySlug}/workhours` : '', { revalidateOnFocus: true });

  useEffect(() => {
    setScheduleData(transformDataFromAPI(data));
    dispatch(setCanAddInterval(true));
  }, [data, dispatch]);

  const changeDuration = (newDuration: number | null) => {
    setScheduleData((prevData) => ({ ...prevData, duration: newDuration }));
    const clearedIntervals: IntervalsObject[] = scheduleData?.timeSlots.map(({ id, is_delete }) => ({
      from: '',
      to: '',
      id: id,
      is_delete: is_delete,
    }));
    setScheduleData((prevData) => ({ ...prevData, timeSlots: [...clearedIntervals] }));
  };

  return (
    <Stack direction='row'>
      <Stack flexBasis='50%' gap={2.5} sx={styles.container1} >
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography sx={styles.headerText}>
            <FormattedMessage defaultMessage='Duration' />
          </Typography>
        </Stack>
        <Typography sx={{ ...styles.bodyText1, color: '#6A6A6A' }}>
          <FormattedMessage defaultMessage='Display available time slots and duration for users' />
        </Typography>
        <SelectDurationDropdown
          selected={scheduleData?.duration}
          setSelected={changeDuration}
          disabledFlag={false}
        />
      </Stack>
      <Divider orientation='vertical' sx={{ height: '100vh' }}/>
      <Stack flexBasis='50%'>
        <WeeklyHours 
          isLoading={isLoading}
          error={error}
          scheduleData={scheduleData}
          setScheduleData={setScheduleData}/>
      </Stack>
    </Stack>
  );
};

export default ListCalendarView;